// CreatureCard.jsx - Mobile-optimized individual creature card component with glassmorphic design
import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Divider,
  useMediaQuery,
  alpha,
} from "@mui/material";
import {
  BugReport,
  LocationOn,
  DateRange,
  Visibility,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { CardHeader, Avatar } from "@mui/material";
import { useTheme, styled } from "@mui/material/styles";
import { useUnits } from "../../contexts/UnitContext";

// Glassmorphic Card
const GlassCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.8)} 0%, 
    ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
  backdropFilter: "blur(20px) saturate(1.1)",
  WebkitBackdropFilter: "blur(20px) saturate(1.1)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
  borderRadius: 20,
  position: "relative",
  overflow: "hidden",
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.06)}`,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  cursor: "pointer",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.primary.main, 0.4)}, 
      ${alpha(theme.palette.secondary.main, 0.3)}, 
      transparent)`,
    borderRadius: "20px 20px 0 0",
    zIndex: 1,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 20%, 
      ${alpha(theme.palette.primary.main, 0.03)} 0%, 
      transparent 50%), 
      radial-gradient(circle at 80% 80%, 
      ${alpha(theme.palette.secondary.main, 0.03)} 0%, 
      transparent 50%)`,
    pointerEvents: "none",
    zIndex: 0,
  },

  "& > *": {
    position: "relative",
    zIndex: 2,
  },

  // Mobile-specific hover effects
  [theme.breakpoints.down("md")]: {
    borderRadius: 16,
    margin: theme.spacing(0, 0.5),
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.1)}`,
      background: `linear-gradient(135deg, 
        ${alpha(theme.palette.background.paper, 0.85)} 0%, 
        ${alpha(theme.palette.background.paper, 0.65)} 100%)`,
      borderColor: alpha(theme.palette.primary.main, 0.2),
    },
    "&:active": {
      transform: "translateY(-1px) scale(0.98)",
      boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.08)}`,
    },
  },

  // Desktop hover effects
  [theme.breakpoints.up("md")]: {
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.12)}`,
      background: `linear-gradient(135deg, 
        ${alpha(theme.palette.background.paper, 0.9)} 0%, 
        ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
      borderColor: alpha(theme.palette.primary.main, 0.25),
      "&::before": {
        background: `linear-gradient(90deg, 
          transparent, 
          ${alpha(theme.palette.primary.main, 0.6)}, 
          ${alpha(theme.palette.secondary.main, 0.5)}, 
          transparent)`,
      },
      "&::after": {
        background: `radial-gradient(circle at 20% 20%, 
          ${alpha(theme.palette.primary.main, 0.05)} 0%, 
          transparent 50%), 
          radial-gradient(circle at 80% 80%, 
          ${alpha(theme.palette.secondary.main, 0.05)} 0%, 
          transparent 50%)`,
      },
    },
  },

  [theme.breakpoints.down("sm")]: {
    borderRadius: 12,
    margin: theme.spacing(0, 0.5),
  },
}));

// Glassmorphic Avatar
const GlassAvatar = styled(Avatar)(({ theme, categoryColor }) => ({
  background: `linear-gradient(135deg, 
    ${categoryColor} 0%, 
    ${alpha(categoryColor, 0.8)} 100%)`,
  border: `2px solid ${alpha(theme.palette.common.white, 0.2)}`,
  boxShadow: `0 4px 16px ${alpha(categoryColor, 0.3)}`,
  position: "relative",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.common.white, 0.2)} 0%, 
      transparent 50%)`,
    borderRadius: "inherit",
    pointerEvents: "none",
  },
}));

// Glassmorphic Chip
const GlassChip = styled(Chip)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.primary.main, 0.08)} 0%, 
    ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
  backdropFilter: "blur(8px)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  color: theme.palette.primary.main,
  fontWeight: 500,

  "&:hover": {
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.primary.main, 0.12)} 0%, 
      ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
  },
}));

// Glassmorphic Category Chip
const GlassCategoryChip = styled(Chip)(({ theme, categoryColor }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(categoryColor, 0.15)} 0%, 
    ${alpha(categoryColor, 0.08)} 100%)`,
  backdropFilter: "blur(8px)",
  border: `1px solid ${alpha(categoryColor, 0.3)}`,
  color: categoryColor,
  fontWeight: 600,

  "&:hover": {
    background: `linear-gradient(135deg, 
      ${alpha(categoryColor, 0.2)} 0%, 
      ${alpha(categoryColor, 0.12)} 100%)`,
  },
}));

// Glassmorphic Divider
const GlassDivider = styled(Divider)(({ theme }) => ({
  background: `linear-gradient(90deg, 
    transparent 0%, 
    ${alpha(theme.palette.divider, 0.6)} 50%, 
    transparent 100%)`,
  height: "1px",
  position: "relative",

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "10%",
    right: "10%",
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent 0%, 
      ${alpha(theme.palette.common.white, 0.1)} 50%, 
      transparent 100%)`,
  },
}));

// Glassmorphic IconButton
const GlassIconButton = styled(IconButton)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.1)} 0%, 
    ${alpha(theme.palette.background.paper, 0.05)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  borderRadius: 12,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

  "&:hover": {
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.primary.main, 0.1)} 0%, 
      ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
    borderColor: alpha(theme.palette.primary.main, 0.3),
    transform: "translateY(-1px)",
    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`,
  },

  "&:active": {
    transform: "translateY(0) scale(0.98)",
  },
}));

// Glassmorphic List
const GlassList = styled(List)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.default, 0.4)} 0%, 
    ${alpha(theme.palette.background.default, 0.2)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: 12,
  position: "relative",
  overflow: "hidden",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.common.white, 0.1)}, 
      transparent)`,
    borderRadius: "12px 12px 0 0",
  },
}));

const CreatureCard = ({ creature, divesites = [] }) => {
  const theme = useTheme();
  const { convert } = useUnits();
  const [expanded, setExpanded] = useState(false);

  // Mobile breakpoint detection
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isXsScreen = useMediaQuery(theme.breakpoints.down("xs"));

  if (!creature) return null;

  const {
    commonName,
    scientificName,
    category,
    totalSightings = 0,
    recentSightings = [],
    divesiteLocations = [],
    lastSeen,
  } = creature;

  // Get unique divesite count
  const uniqueDivesites = divesiteLocations?.length || 0;

  // Get most recent sighting date
  const mostRecentDate = recentSightings?.[0]?.timestamp || lastSeen;

  // Color mapping for creature categories
  const getCategoryColor = (category) => {
    const colors = {
      fish: theme.palette.info.main,
      invertebrate: theme.palette.warning.main,
      mammal: theme.palette.success.main,
      reptile: theme.palette.error.main,
      plant: theme.palette.primary.main,
      other: theme.palette.grey[500],
    };
    return colors[category?.toLowerCase()] || colors.other;
  };

  const categoryColor = getCategoryColor(category);

  const formatDate = (dateString) => {
    if (isMobile) {
      // Shorter date format for mobile
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "2-digit",
      });
    }
    return new Date(dateString).toLocaleDateString();
  };

  // Truncate text for mobile with option to show full text
  const truncateText = (text, maxLength = 25) => {
    if (!isMobile || !text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <GlassCard>
      <CardHeader
        avatar={
          <GlassAvatar
            categoryColor={categoryColor}
            sx={{
              width: isMobile ? 40 : 48,
              height: isMobile ? 40 : 48,
            }}
          >
            <BugReport fontSize={isMobile ? "small" : "medium"} />
          </GlassAvatar>
        }
        title={
          <Typography
            variant={isMobile ? "subtitle1" : "h6"}
            fontWeight="bold"
            sx={{
              color: "primary.main",
              whiteSpace: isMobile ? "normal" : "nowrap",
              overflow: "visible",
              textOverflow: "unset",
              lineHeight: isMobile ? 1.2 : 1.6,
            }}
          >
            {commonName || "Unknown Creature"}
          </Typography>
        }
        subheader={
          <Typography
            variant="caption"
            sx={{
              color: (theme) => alpha(theme.palette.text.secondary, 0.8),
              whiteSpace: isMobile ? "normal" : "nowrap",
              overflow: "visible",
              textOverflow: "unset",
              fontSize: isMobile ? "0.7rem" : "0.75rem",
            }}
          >
            {isMobile
              ? truncateText(scientificName, 30)
              : scientificName || "Species unknown"}
          </Typography>
        }
        action={
          <GlassCategoryChip
            categoryColor={categoryColor}
            label={category || "Other"}
            size="small"
            sx={{
              fontSize: isMobile ? "0.65rem" : "0.75rem",
              height: isMobile ? 24 : 32,
            }}
          />
        }
        sx={{
          pb: isMobile ? 1 : 2,
          "& .MuiCardHeader-content": {
            minWidth: 0,
          },
        }}
      />

      <CardContent
        sx={{
          flexGrow: 1,
          pt: 0,
          px: isMobile ? 2 : 3,
          pb: isMobile ? 1 : 2,
        }}
      >
        {/* Key Stats */}
        <Stack
          direction="row"
          spacing={isMobile ? 1 : 2}
          sx={{ mb: isMobile ? 1.5 : 2 }}
        >
          <Box sx={{ textAlign: "center", flex: 1 }}>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              fontWeight="bold"
              color="primary"
            >
              {totalSightings}
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ fontSize: isMobile ? "0.65rem" : "0.75rem" }}
            >
              {isMobile ? "Sightings" : "Total Sightings"}
            </Typography>
          </Box>

          <GlassDivider orientation="vertical" flexItem />

          <Box sx={{ textAlign: "center", flex: 1 }}>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              fontWeight="bold"
              color="secondary"
            >
              {uniqueDivesites}
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ fontSize: isMobile ? "0.65rem" : "0.75rem" }}
            >
              {isMobile ? "Sites" : "Dive Sites"}
            </Typography>
          </Box>
        </Stack>

        {/* Last Seen */}
        {mostRecentDate && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: isMobile ? 1.5 : 2,
              flexWrap: isMobile ? "wrap" : "nowrap",
            }}
          >
            <DateRange
              fontSize="small"
              sx={{
                mr: 1,
                color: "text.secondary",
                filter: `drop-shadow(0 2px 4px ${alpha(
                  theme.palette.primary.main,
                  0.2
                )})`,
              }}
            />
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}
            >
              Last seen: {formatDate(mostRecentDate)}
            </Typography>
          </Box>
        )}

        {/* Top Locations Preview */}
        {divesiteLocations && divesiteLocations.length > 0 && (
          <Box sx={{ mb: isMobile ? 1.5 : 2 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontSize: isMobile ? "0.8rem" : "0.875rem" }}
            >
              <LocationOn
                fontSize="small"
                sx={{
                  mr: 0.5,
                  verticalAlign: "middle",
                  filter: `drop-shadow(0 2px 4px ${alpha(
                    theme.palette.primary.main,
                    0.2
                  )})`,
                }}
              />
              {isMobile ? "Locations" : "Top Locations"}
            </Typography>

            <Stack
              direction={isMobile ? "column" : "row"}
              spacing={isMobile ? 0.5 : 1}
              sx={{
                flexWrap: isMobile ? "nowrap" : "wrap",
                gap: isMobile ? 0.5 : 1,
              }}
            >
              {divesiteLocations
                .slice(0, isMobile ? 3 : 2)
                .map((location, index) => (
                  <GlassChip
                    key={index}
                    label={
                      isMobile ? truncateText(location.name, 25) : location.name
                    }
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: isMobile ? "0.65rem" : "0.75rem",
                      height: isMobile ? 24 : 32,
                      alignSelf: isMobile ? "flex-start" : "auto",
                      maxWidth: isMobile ? "100%" : "auto",
                      cursor: "default",
                    }}
                    title={location.name}
                    clickable={false}
                    onClick={() => {}}
                  />
                ))}
              {divesiteLocations.length > (isMobile ? 3 : 2) && (
                <GlassChip
                  label={`+${
                    divesiteLocations.length - (isMobile ? 3 : 2)
                  } more`}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: isMobile ? "0.65rem" : "0.75rem",
                    height: isMobile ? 24 : 32,
                    alignSelf: isMobile ? "flex-start" : "auto",
                      cursor: "default",
                  }}
                    clickable={false}
                    onClick={() => {}}
                />
              )}
            </Stack>
          </Box>
        )}

        {/* Expand/Collapse for detailed view */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <GlassIconButton
            onClick={() => setExpanded(!expanded)}
            size={isMobile ? "large" : "small"}
            sx={{
              minHeight: isMobile ? 44 : 32,
              px: isMobile ? 2 : 1,
            }}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
            <Typography
              variant="caption"
              sx={{
                ml: 0.5,
                fontSize: isMobile ? "0.7rem" : "0.75rem",
              }}
            >
              {expanded ? "Less" : "More"} Details
            </Typography>
          </GlassIconButton>
        </Box>

        {/* Expanded Details */}
        <Collapse in={expanded}>
          <Box sx={{ mt: isMobile ? 1.5 : 2 }}>
            {/* Recent Sightings */}
            {recentSightings && recentSightings.length > 0 && (
              <Box sx={{ mb: isMobile ? 1.5 : 2 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontSize: isMobile ? "0.8rem" : "0.875rem" }}
                >
                  <Visibility
                    fontSize="small"
                    sx={{
                      mr: 0.5,
                      verticalAlign: "middle",
                      filter: `drop-shadow(0 2px 4px ${alpha(
                        theme.palette.primary.main,
                        0.2
                      )})`,
                    }}
                  />
                  Recent Sightings
                </Typography>
                <GlassList
                  dense={!isMobile}
                  sx={{
                    py: isMobile ? 0.5 : 0,
                  }}
                >
                  {recentSightings
                    .slice(0, isMobile ? 2 : 3)
                    .map((sighting, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          py: isMobile ? 1 : 0.5,
                          px: isMobile ? 1.5 : 2,
                        }}
                      >
                        <ListItemText
                          primary={
                            isMobile
                              ? truncateText(
                                  sighting.divesiteName || "Unknown Location",
                                  30
                                )
                              : sighting.divesiteName || "Unknown Location"
                          }
                          secondary={
                            <Box>
                              <Typography
                                variant="caption"
                                display="block"
                                sx={{
                                  fontSize: isMobile ? "0.65rem" : "0.75rem",
                                }}
                              >
                                {formatDate(sighting.timestamp)}
                              </Typography>
                              {(sighting.divesiteCity ||
                                sighting.divesiteProvince) && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{
                                    fontSize: isMobile ? "0.6rem" : "0.7rem",
                                  }}
                                >
                                  {[
                                    sighting.divesiteCity,
                                    sighting.divesiteProvince,
                                  ]
                                    .filter(Boolean)
                                    .join(", ")}
                                </Typography>
                              )}
                            </Box>
                          }
                          primaryTypographyProps={{
                            variant: "body2",
                            sx: { fontSize: isMobile ? "0.8rem" : "0.875rem" },
                          }}
                        />
                      </ListItem>
                    ))}
                </GlassList>
              </Box>
            )}

            {/* All Locations */}
            {divesiteLocations &&
              divesiteLocations.length > (isMobile ? 3 : 2) && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontSize: isMobile ? "0.8rem" : "0.875rem" }}
                  >
                    All Locations ({divesiteLocations.length})
                  </Typography>
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    spacing={isMobile ? 0.5 : 0.5}
                    sx={{
                      flexWrap: isMobile ? "nowrap" : "wrap",
                      gap: isMobile ? 0.5 : 0.5,
                      maxHeight: isMobile ? 200 : "auto",
                      overflowY: isMobile ? "auto" : "visible",
                    }}
                  >
                    {divesiteLocations.map((location, index) => {
                      const locationParts = [location.name];
                      if (location.city) locationParts.push(location.city);
                      if (location.province)
                        locationParts.push(location.province);

                      const locationText = locationParts.join(", ");
                      const chipLabel = `${locationText} (${location.count})`;
                      const displayLabel = isMobile
                        ? truncateText(chipLabel, 35)
                        : chipLabel;

                      return (
                        <GlassChip
                          key={index}
                          label={displayLabel}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontSize: isMobile ? "0.65rem" : "0.7rem",
                            height: isMobile ? 24 : 32,
                            maxWidth: isMobile ? "100%" : "300px",
                            alignSelf: isMobile ? "flex-start" : "auto",
                          }}
                          title={chipLabel}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              )}
          </Box>
        </Collapse>
      </CardContent>
    </GlassCard>
  );
};

export default CreatureCard;
